<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core'
  import {
    TooltipArrow,
    TooltipContent,
    type TooltipContentEmits,
    type TooltipContentProps,
    TooltipPortal,
    useForwardPropsEmits
  } from 'reka-ui'
  import type { HTMLAttributes } from 'vue'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'
  import { cn } from '../../../lib/utils'
  import { injectTooltipRootContext } from 'reka-ui'
  import { computed } from 'vue'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<
      TooltipContentProps & {
        class?: HTMLAttributes['class']
        arrowClass?: HTMLAttributes['class']
      }
    >(),
    {
      sideOffset: 4
    }
  )

  const emits = defineEmits<TooltipContentEmits>()

  const delegatedProps = reactiveOmit(props, 'class')
  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const tooltipContext = injectTooltipRootContext()

  const open = computed(() => tooltipContext.open.value)

  const { zIndex } = useDialogZIndex(open)
</script>

<template>
  <TooltipPortal>
    <TooltipContent
      data-slot="tooltip-content"
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'w-fit rounded-md bg-black/90 px-3 py-1.5 text-xs text-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          props.class
        )
      "
      :style="{
        zIndex
      }"
    >
      <slot />

      <TooltipArrow
        :class="
          cn(
            'z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-black/90 fill-black/90 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            props.arrowClass
          )
        "
      />
    </TooltipContent>
  </TooltipPortal>
</template>
